import { Component, Input } from '@angular/core';
import { FormGroupDirective, FormControl, ReactiveFormsModule, ControlContainer, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { SelectFormFieldComponent } from '../fields/select-form-field/select-form-field.component';
import { TextareaFormFieldComponent } from '../fields/textarea-form-field/textarea-form-field.component';
import { InputFormFieldComponent } from '../fields/input-form-field/input-form-field.component';
import { DatepickerFormFieldComponent } from '../fields/datepicker-form-field/datepicker-form-field.component';
import { CheckboxFormFieldComponent } from '../fields/checkbox-form-field/checkbox-form-field.component';
import { ConfigurationService } from 'app/shared/services/configuration.service';
import { DropdownEntryEnum, DropdownTypeEnum } from 'app/shared/enums/dropdowns.enum';
import { take } from 'rxjs';
import { ConfigurationSelectFormFieldComponent } from '../fields/configuration-select-form-field/configuration-select-form-field.component';
import { CommonModule } from '@angular/common';
import { HelperService } from 'app/shared/services/helper.service';
import { CaseFormModel } from 'app/case-form/case-form.model';
import { CaseFormService } from 'app/case-form/case-form.service';

@Component({
  selector: 'app-product-details-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TextareaFormFieldComponent,
    SelectFormFieldComponent,
    InputFormFieldComponent,
    DatepickerFormFieldComponent,
    CheckboxFormFieldComponent,
    TranslateModule,
    ConfigurationSelectFormFieldComponent,
    CommonModule
  ],
  templateUrl: './product-details-form.component.html',
  styleUrl: './product-details-form.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class ProductDetailsFormComponent {
  @Input() caseId!: number;
  @Input() extended!: boolean;
  @Input() viewMode!: boolean;
  @Input() case!: CaseFormModel;
  productGroupItems: any[] = [];
  productSubGroupItems: any[] = [];

  form: any;
  dropdownTypeEnum = DropdownTypeEnum;
  datepickerMaxDate: Date = new Date();
  dropdownType = DropdownTypeEnum.PRODUCT_INFORMATION;
  dropdownEquipmentStatusType = DropdownTypeEnum.EQUIPMENT_STATUS;
  entry1 = DropdownEntryEnum.ENTRY_1;
  entry2 = DropdownEntryEnum.ENTRY_2;
  entry3 = DropdownEntryEnum.ENTRY_3;
  entry4 = DropdownEntryEnum.ENTRY_4;
  entry5 = DropdownEntryEnum.ENTRY_5;

  constructor(
    public mainForm: FormGroupDirective,
    private configurationService: ConfigurationService,
    private helperService: HelperService,
    private caseFormService: CaseFormService
  ) { }

  ngOnChanges() {
    if (this.case) {
      this.helperService.enableDisableDependentFieldOnSelect(this.form, this.case.productFamily, 'productGroup');
      this.helperService.enableDisableDependentFieldOnSelect(this.form, this.case.productGroup, 'productSubGroup');
    }
  }

  ngOnInit(): void {
    this.createForm();
  }

  createForm() {
    this.form = this.mainForm.form;

    this.form.addControl('productFamily', new FormControl(null, [Validators.required]));
    this.form.addControl('productGroup', new FormControl({ value: null, disabled: !this.viewMode }, [Validators.required]));
    this.form.addControl('productSubGroup', new FormControl({ value: null, disabled: !this.viewMode }, [Validators.required]));
    this.form.addControl('serialNumber', new FormControl(null, [Validators.required]));
    this.form.addControl('softwareVersion', new FormControl(null, []));
    this.form.addControl('runTime', new FormControl(null, []));
    this.form.addControl('equipmentStatus', new FormControl(null, [Validators.required]));
    this.form.addControl('partNumber', new FormControl(null, []));
    this.form.addControl('partsAdditonalInfo', new FormControl(null, []));
    this.form.addControl('productComponentVarient', new FormControl(null, []));
    this.form.addControl('productCode', new FormControl(null, []));
    this.form.addControl('materialNumber', new FormControl(null, []));
    this.form.addControl('additonalProductInfo', new FormControl(null, []));
    this.form.addControl('parsReference', new FormControl(null, []));
    this.form.addControl('rmaId', new FormControl(null, []));
    this.form.addControl('installDate', new FormControl(null, []));
    this.form.addControl('partsRequested', new FormControl(false, []));
    this.form.addControl('warranty', new FormControl(false, []));
    this.form.addControl('faRequested', new FormControl(false, []));
    this.form.addControl('foi', new FormControl(false, []));
    this.form.addControl('doa', new FormControl(false, []));
    this.form.addControl('goodwill', new FormControl(false, []));
    this.form.addControl('ktUsed', new FormControl(false, []));
    this.form.addControl('partsArrived', new FormControl(false, []));
    this.form.addControl('orderNumber', new FormControl(null, []));
    this.form.addControl('trackingNumber', new FormControl(null, []));
  }

  onSelectProductFields(data: any, field: string) {
    this.resetProductFields(field, data);

    if (!data) {
      return;
    }

    let params = {
      type: this.dropdownType,
      entry1: this.form.get('productFamily')?.value || '',
      entry2: this.form.get('productGroup')?.value || '',
      entry3: this.form.get('productSubGroup')?.value || '',
    };

    if (!params.entry1) {
      return;
    }

    let entry = this.entry2;

    if (field === DropdownTypeEnum.PRODUCT_SUB_GROUP) {
      if (!params.entry2) {
        return;
      }
      entry = this.entry3;
    }

    this.configurationService.getDropdownConfigurations(params, entry)
      .pipe(take(1))
      .subscribe((res: any) => {
        switch (field) {
          case DropdownTypeEnum.PRODUCT_GROUP:
            this.productGroupItems = res;
            break;
          case DropdownTypeEnum.PRODUCT_SUB_GROUP:
            this.productSubGroupItems = res;
            break;
        }
      });

  }

  onSelectProductSubGroupField(data: any) {
    if (!this.caseId || !data) {
      return;
    }

    this.resetProductSuiteProductCompany();

    const productCompanyParams = {
      type: this.dropdownType,
      entry1: this.form.get('productFamily')?.value || '',
      entry2: this.form.get('productGroup')?.value || '',
      entry3: this.form.get('productSubGroup')?.value || '',
      entry4: ''
    };

    this.configurationService.getSingleDropdownConfigurations(productCompanyParams)
      .pipe(take(1))
      .subscribe((res: any) => {
        this.form.get('productCompany')?.setValue(res ? res.entry4 : null);
        this.form.get('productSuite')?.setValue(res ? res.entry5 : null);

        this.caseFormService.onChangeProductSuiteAndCompanyEmitter.emit(this.form.get('caseStage')?.value);
      });

  }

  resetProductSuiteProductCompany() {
    this.form.get('productSuite')?.setValue(null);
    this.form.get('productCompany')?.setValue(null);
    this.form.get('rootCauseSystemComponent')?.setValue(null);
    this.form.get('rootCauseCategory')?.setValue(null);
  }

  resetProductFields(resetField: string, data: any) {
    if (resetField === DropdownTypeEnum.PRODUCT_GROUP) {
      this.form.get('productGroup').setValue(null);
      this.productGroupItems = [];
      this.helperService.enableDisableDependentFieldOnSelect(this.form, data, 'productGroup');
      
      this.resetProductFields(DropdownTypeEnum.PRODUCT_SUB_GROUP, null);
    }

    if (resetField === DropdownTypeEnum.PRODUCT_SUB_GROUP) {
      this.form.get('productSubGroup').setValue(null);
      this.productSubGroupItems = [];
      this.helperService.enableDisableDependentFieldOnSelect(this.form, data, 'productSubGroup');
    }

    this.resetProductSuiteProductCompany();

  }

}
